import type { StatItem, StatsAlign, StatsProps, StatsSize } from "@emkit/builder-widgets";
import { useEditor } from "../../store.js";
import { CheckboxField, ColorField, SelectField, TextField } from "./fields.js";
import type { WidgetInspectorProps } from "./registry.js";

const COLS = ["2", "3", "4"] as const;
const SIZE = ["sm", "md", "lg"] as const;
const ALIGN = ["start", "center", "end"] as const;

function newItemId(): string {
  return `st-${Math.random().toString(36).slice(2, 8)}`;
}

export function StatsInspector({ widget }: WidgetInspectorProps) {
  const update = useEditor((s) => s.updateWidgetProps);
  const p = widget.props as StatsProps;
  const items = p.items ?? [];

  const setItems = (next: StatItem[]) => update(widget.id, { items: next });

  const addItem = () =>
    setItems([...items, { id: newItemId(), value: "100", label: "Things" }]);

  const updateItem = (id: string, patch: Partial<StatItem>) =>
    setItems(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  const removeItem = (id: string) => setItems(items.filter((it) => it.id !== id));

  const moveItem = (id: string, dir: "up" | "down") => {
    const idx = items.findIndex((it) => it.id === id);
    if (idx < 0) return;
    const target = dir === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= items.length) return;
    const next = items.slice();
    const moved = next.splice(idx, 1)[0];
    if (!moved) return;
    next.splice(target, 0, moved);
    setItems(next);
  };

  return (
    <>
      <SelectField
        label="Columns"
        value={String(p.columns ?? 4) as (typeof COLS)[number]}
        options={COLS}
        onChange={(v) =>
          update(widget.id, {
            columns: Number.parseInt(v, 10) as StatsProps["columns"],
          })
        }
      />
      <SelectField
        label="Size"
        value={p.size ?? "md"}
        options={SIZE}
        onChange={(v) => update(widget.id, { size: v as StatsSize })}
      />
      <SelectField
        label="Align"
        value={p.align ?? "center"}
        options={ALIGN}
        onChange={(v) => update(widget.id, { align: v as StatsAlign })}
      />
      <CheckboxField
        label="Show dividers between cells"
        value={p.divided ?? false}
        onChange={(v) => update(widget.id, { divided: v })}
      />
      <ColorField
        label="Value colour"
        value={p.valueColor ?? "#111827"}
        onChange={(v) => update(widget.id, { valueColor: v })}
      />
      <ColorField
        label="Label colour"
        value={p.labelColor ?? "#6b7280"}
        onChange={(v) => update(widget.id, { labelColor: v })}
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Stats ({items.length})
      </div>

      {items.map((it, i) => (
        <div
          key={it.id}
          style={{
            padding: "8px 16px 10px",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 4,
              justifyContent: "flex-end",
              marginBottom: 4,
            }}
          >
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveItem(it.id, "up")}
              disabled={i === 0}
              title="Move up"
              style={{ padding: "2px 6px", opacity: i === 0 ? 0.4 : 1 }}
            >
              ↑
            </button>
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveItem(it.id, "down")}
              disabled={i === items.length - 1}
              title="Move down"
              style={{
                padding: "2px 6px",
                opacity: i === items.length - 1 ? 0.4 : 1,
              }}
            >
              ↓
            </button>
            <button
              type="button"
              className="emke-btn is-danger"
              onClick={() => removeItem(it.id)}
              title="Delete"
              style={{ padding: "2px 6px" }}
            >
              ✕
            </button>
          </div>

          <div className="emke-inspector-grid-2" style={{ padding: "0" }}>
            <TextField
              label={`Value ${i + 1}`}
              value={it.value}
              onChange={(v) => updateItem(it.id, { value: v })}
            />
            <TextField
              label="Label"
              value={it.label}
              onChange={(v) => updateItem(it.id, { label: v })}
            />
          </div>
          <div className="emke-inspector-grid-2" style={{ padding: "0" }}>
            <TextField
              label="Prefix"
              value={it.prefix ?? ""}
              onChange={(v) => updateItem(it.id, { prefix: v || undefined })}
            />
            <TextField
              label="Suffix"
              value={it.suffix ?? ""}
              onChange={(v) => updateItem(it.id, { suffix: v || undefined })}
            />
          </div>
        </div>
      ))}

      <div style={{ padding: "12px 16px 0" }}>
        <button
          type="button"
          className="emke-btn"
          onClick={addItem}
          style={{ width: "100%" }}
        >
          + Add stat
        </button>
      </div>
    </>
  );
}
